import React, { useEffect, useMemo, useRef, useState } from 'react';
import AnnotationStage from '../Video.js/componnets/Annotations';

// Lightweight HTML5 video player for baseline debugging
// Props (subset):
// - videoContent: { videoContentUrl: string }
// - videoRef: React ref to the <video> element
// - poster?: string
// - autoplay?: boolean
// - muted?: boolean
// - controls?: boolean (default true)
// - setLoading?: fn => called false on canplay
// - onError?: fn(error)
// - annotations?: [] list with { timeRange: { startTime, endTime }, markerDetails, comments }
// - setAnnotations?: fn(list)
// - addStandaloneFeedback?: fn(payload) optional
// - showAnnotations?: boolean (default true)
export default function Html5VideoPlayer({
  videoContent,
  videoRef,
  poster,
  autoplay = false,
  muted = true,
  controls = true,
  setLoading,
  onError,
  className,
  style,
  // annotations features
  annotations = [],
  setAnnotations,
  addStandaloneFeedback,
  // new: pending-box flow
  pendingAnnotation, // { x1,y1,x2,y2 } normalized or null
  onPendingAnnotationChange, // function(coords|null)
  showAnnotations = true,
  isPlayerSide = true,
}) {
  const localRef = useRef(null);
  const ref = videoRef || localRef;
  const src = videoContent?.videoContentUrl || '';
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [current, setCurrent] = useState(0);
  const [rate, setRate] = useState(0.5);
  const [volume, setVolume] = useState(1);
  const [mutedState, setMutedState] = useState(!!muted);
  const [isPiP, setIsPiP] = useState(false);
  // Drawing state for Konva overlay (controlled implicitly by pause state; no UI toggle)
  const [drawing, setDrawing] = useState(false);
  const [newAnnotation, setNewAnnotation] = useState(null);

  // LOG ONLY: Warn developer if browser doesn't support HEVC
  useEffect(() => {
    const video = document.createElement('video');
    const canPlayHevc = video.canPlayType('video/mp4; codecs="hvc1"');
    if (!canPlayHevc) {
      console.warn('⚠️ [Video Playback] Video format appears to be HEVC (H.265). This browser (likely Chrome) does not support it natively. Please use Safari or re-encode video to H.264.');
    }
  }, []);

  // Show preview for pending annotation (normalized) so user sees the box before saving
  useEffect(() => {
    if (pendingAnnotation && pendingAnnotation.x1 != null) {
      setNewAnnotation({ markerDetails: { coordinates: pendingAnnotation } });
    } else {
      setNewAnnotation(null);
    }
  }, [pendingAnnotation]);

  // When src changes, we must manually reload because we used <source> tags
  // instead of src attribute on <video> (to support type="video/mp4" for S3)
  useEffect(() => {
    if (ref.current && src) {
      ref.current.load();
    }
  }, [src]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const handleCanPlay = () => {
      try { if (typeof setLoading === 'function') setLoading(false); } catch { }
    };
    const handleError = () => {
      const err = el?.error;
      if (typeof onError === 'function') onError(err);
      // eslint-disable-next-line no-console
      console.error('[Html5VideoPlayer] Video error details:', {
        code: err?.code,
        message: err?.message,
        networkState: el?.networkState,
        readyState: el?.readyState,
        src: el?.currentSrc || src
      });
    };
    const handleLoadedMetadata = () => {
      setDuration(Number(el.duration) || 0);
    };
    const handleTimeUpdate = () => {
      setCurrent(Number(el.currentTime) || 0);
    };
    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);
    const handleVolumeChange = () => {
      try {
        setVolume(Number(el.volume));
        setMutedState(!!el.muted);
      } catch { }
    };

    el.addEventListener('canplay', handleCanPlay);
    el.addEventListener('error', handleError);
    el.addEventListener('loadedmetadata', handleLoadedMetadata);
    el.addEventListener('timeupdate', handleTimeUpdate);
    el.addEventListener('play', handlePlay);
    el.addEventListener('pause', handlePause);
    el.addEventListener('volumechange', handleVolumeChange);

    return () => {
      try { el.removeEventListener('canplay', handleCanPlay); } catch { }
      try { el.removeEventListener('error', handleError); } catch { }
      try { el.removeEventListener('loadedmetadata', handleLoadedMetadata); } catch { }
      try { el.removeEventListener('timeupdate', handleTimeUpdate); } catch { }
      try { el.removeEventListener('play', handlePlay); } catch { }
      try { el.removeEventListener('pause', handlePause); } catch { }
      try { el.removeEventListener('volumechange', handleVolumeChange); } catch { }
    };
  }, [ref, setLoading, onError]);

  // Auto-play if requested and allowed by browser policies
  useEffect(() => {
    const el = ref.current;
    if (!el || !autoplay) return;
    const tryPlay = async () => {
      try { await el.play(); } catch { }
    };
    tryPlay();
  }, [ref, autoplay, src]);

  // Ensure default playback rate is applied to the video element
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    try { el.playbackRate = rate; } catch { }
  }, [ref, rate, src]);

  // Global keyboard shortcuts (ignored while typing in inputs/textareas or contentEditable)
  useEffect(() => {
    const handleKeyDown = (e) => {
      const active = document.activeElement;
      const tag = (active && active.tagName) || '';
      const isTyping = tag === 'INPUT' || tag === 'TEXTAREA' || (active && active.isContentEditable);
      if (isTyping) return;
      // Ignore auto-repeat to prevent rapid-fire seeks
      if (e.repeat) return;

      const el = ref.current;
      const hasCtrl = e.ctrlKey || e.metaKey; // support Cmd on mac
      const key = e.key;
      const code = e.code;
      // Simple throttle for seek actions
      const now = Date.now();
      const throttleOk = () => {
        if (now - (lastSeekAtRef.current || 0) < 120) return false;
        lastSeekAtRef.current = now;
        return true;
      };

      // Space: Play/Pause
      if (key === ' ' || code === 'Space') {
        e.preventDefault();
        e.stopPropagation();
        togglePlay();
        return;
      }

      // Arrow Left/Right: seek 5s
      if (code === 'ArrowLeft') { e.preventDefault(); if (throttleOk()) seekBy(-5); return; }
      if (code === 'ArrowRight') { e.preventDefault(); if (throttleOk()) seekBy(5); return; }

      // Arrow Up/Down: volume up/down
      if (code === 'ArrowUp') { e.preventDefault(); changeVolume(Math.min(1, (Number(el?.volume) || 0) + 0.05)); return; }
      if (code === 'ArrowDown') { e.preventDefault(); changeVolume(Math.max(0, (Number(el?.volume) || 0) - 0.05)); return; }

      // PageUp/PageDown: seek 10s
      if (code === 'PageUp') { e.preventDefault(); if (throttleOk()) seekBy(10); return; }
      if (code === 'PageDown') { e.preventDefault(); if (throttleOk()) seekBy(-10); return; }

      // Home/End: jump to start/end
      if (code === 'Home') { e.preventDefault(); try { if (el) el.currentTime = 0; } catch { } return; }
      if (code === 'End') { e.preventDefault(); try { if (el) el.currentTime = Number(el.duration) || 0; } catch { } return; }

      // 0-9: seek to percentage (10%-90%), 0 -> 0%
      if (/^[0-9]$/.test(key)) {
        e.preventDefault();
        const pct = key === '0' ? 0 : Number(key) * 10;
        const dur = Number(el?.duration) || 0;
        try { if (el && dur > 0) { setSeekingGuard(350); el.currentTime = (pct / 100) * dur; } } catch { }
        return;
      }

      // Ctrl/Cmd + F: Fullscreen
      if (hasCtrl && (key.toLowerCase() === 'f')) { e.preventDefault(); toggleFullscreen(); return; }
      // Ctrl/Cmd + P: Picture-in-Picture
      if (hasCtrl && (key.toLowerCase() === 'p')) { e.preventDefault(); togglePiP(); return; }
      // Ctrl/Cmd + S: Screenshot
      if (hasCtrl && (key.toLowerCase() === 's')) { e.preventDefault(); takeScreenshot(); return; }
      // Ctrl/Cmd + M: Modes button
      if (hasCtrl && (key.toLowerCase() === 'm')) { e.preventDefault(); fireModesEvent(); return; }
      // F1 or Ctrl/Cmd + H: Help
      if (code === 'F1' || (hasCtrl && key.toLowerCase() === 'h')) { e.preventDefault(); showKeyboardShortcuts(); return; }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      try { window.removeEventListener('keydown', handleKeyDown); } catch { }
    };
  }, [ref]);

  // Simple playerRef shim for AnnotationStage API compatibility
  const playerRefShim = useMemo(() => ({
    current: {
      currentTime: (t) => {
        const el = ref.current;
        if (!el) return 0;
        if (typeof t === 'number') {
          try { el.currentTime = Math.max(0, Math.min(Number(el.duration) || 0, t)); } catch { }
        }
        return Number(el.currentTime) || 0;
      },
      play: () => { try { ref.current && ref.current.play(); } catch { } },
      pause: () => { try { ref.current && ref.current.pause(); } catch { } },
      paused: () => { try { return !ref.current || ref.current.paused; } catch { return true; } },
    },
  }), [ref]);

  // Throttle and guard seeking to avoid spamming range requests
  const lastSeekAtRef = useRef(0);
  const seekingGuardRef = useRef(false);
  const setSeekingGuard = (ms = 350) => {
    seekingGuardRef.current = true;
    try { clearTimeout(setSeekingGuard._t); } catch { }
    setSeekingGuard._t = setTimeout(() => { seekingGuardRef.current = false; }, ms);
  };

  const doSeekBy = (delta) => {
    const el = ref.current;
    if (!el) return;
    try {
      const next = Math.max(0, Math.min((Number(el.duration) || 0), Number(el.currentTime || 0) + delta));
      el.currentTime = next;
    } catch { }
  };
  const seekBy = (delta) => {
    setSeekingGuard(350);
    doSeekBy(delta);
  };

  const togglePlay = () => {
    const el = ref.current;
    if (!el) return;
    try {
      if (el.paused) {
        el.play().catch(err => {
          console.error('🎬 [Html5VideoPlayer] play() failed:', err.name, err.message);
        });
      } else {
        el.pause();
      }
    } catch (err) {
      console.error('🎬 [Html5VideoPlayer] togglePlay error:', err);
    }
  };

  const changeRate = (r) => {
    const el = ref.current;
    if (!el) return;
    try { el.playbackRate = r; setRate(r); } catch { }
  };

  const toggleMute = () => {
    const el = ref.current; if (!el) return;
    try { el.muted = !el.muted; setMutedState(el.muted); } catch { }
  };

  const changeVolume = (v) => {
    const el = ref.current; if (!el) return;
    try { el.volume = v; setVolume(v); if (el.muted && v > 0) { el.muted = false; setMutedState(false); } } catch { }
  };

  const handleProgressClick = (e) => {
    const bar = e.currentTarget;
    const rect = bar.getBoundingClientRect();
    const pct = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    const t = pct * (duration || 0);
    const el = ref.current;
    if (!el) return;
    try { el.currentTime = t; } catch { }
  };

  // (Optional add feedback functionality was intentionally omitted to match VideojsPlayer control set)

  const takeScreenshot = () => {
    const el = ref.current;
    if (!el) return;
    try {
      const canvas = document.createElement('canvas');
      canvas.width = el.videoWidth || 640;
      canvas.height = el.videoHeight || 360;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(el, 0, 0, canvas.width, canvas.height);
      const image = canvas.toDataURL('image/png');
      const link = document.createElement('a');
      link.href = image;
      link.download = `screenshot-${Date.now()}.png`;
      link.click();
    } catch (err) {
      console.error("Screenshot failed:", err);
      alert("Failed to take screenshot. This might be due to security restrictions on the video source.");
    }
  };

  // Moved to component scope (not inside useEffect) to satisfy hooks/identifiers
  const fsContainerRef = useRef(null);
  const togglePiP = async () => {
    const el = ref.current; if (!el) return;
    try {
      if (document.pictureInPictureElement) {
        await document.exitPictureInPicture();
        setIsPiP(false);
      } else if (document.pictureInPictureEnabled && !el.disablePictureInPicture) {
        await el.requestPictureInPicture();
        setIsPiP(true);
      }
    } catch { }
  };
  const toggleFullscreen = async () => {
    const container = fsContainerRef.current; if (!container) return;
    try {
      if (!document.fullscreenElement) {
        await container.requestFullscreen();
      } else {
        await document.exitFullscreen();
      }
    } catch { }
  };
  const showKeyboardShortcuts = () => {
    const shortcuts = [
      'Spacebar: Play/Pause',
      '← → Arrow Keys: Seek 5 seconds',
      '↑ ↓ Arrow Keys: Volume up/down',
      'Page Up/Down: Seek 10 seconds',
      'Home/End: Seek to beginning/end',
      '0-9 Keys: Seek to percentage (10%-90%)',
      'Ctrl+F: Fullscreen toggle',
      'Ctrl+P: Picture-in-Picture',
      'Ctrl+S: Take screenshot',
      'Ctrl+M: Modes button',
      'F1 or Ctrl+H: Show this help'
    ];
    // eslint-disable-next-line no-alert
    alert('🎮 Keyboard Shortcuts:\n\n' + shortcuts.join('\n'));
  };
  const fireModesEvent = () => {
    try { document.dispatchEvent(new CustomEvent('modesButtonClicked')); } catch { }
  };

  const markerPercents = useMemo(() => {
    if (!Array.isArray(annotations) || !duration) return [];
    return annotations
      .map((a) => {
        const t = (a?.timeRange?.startTime ?? a?.timeSec);
        const time = Number(t);
        if (!Number.isFinite(time)) return null;
        // Prefer readable label; avoid dumping audio data URLs
        const c0 = a?.comments?.[0];
        const rawBody = c0?.body;
        const label = typeof rawBody === 'string' && rawBody.startsWith('data:audio')
          ? 'Audio'
          : (rawBody || a?.text || 'Annotation');
        return {
          pct: Math.max(0, Math.min(100, (time / duration) * 100)),
          label,
        };
      })
      .filter(Boolean)
      .sort((x, y) => x.pct - y.pct);
  }, [annotations, duration]);

  // Marker times in seconds, sorted ascending
  const markerTimes = useMemo(() => {
    if (!Array.isArray(annotations)) return [];
    return annotations
      .map((a) => Number(a?.timeRange?.startTime ?? a?.timeSec))
      .filter((n) => Number.isFinite(n))
      .sort((a, b) => a - b);
  }, [annotations]);

  // Track last observed time and last marker we paused at
  const lastTimeRef = useRef(0);
  const lastPauseAtRef = useRef(null); // number | null

  // Auto-pause at exact marker time when crossing it during playback
  useEffect(() => {
    const el = ref.current;
    if (!el || !markerTimes.length) { lastTimeRef.current = current; return; }
    const now = Number(el.currentTime);
    const prev = Number(lastTimeRef.current) || 0;

    // Update for next tick early to avoid re-entrancy issues
    lastTimeRef.current = now;

    // Only act while playing and moving forward
    if (!isPlaying || !(now >= prev)) return;

    const EPS = 0.04; // ~40ms tolerance (~1 frame at 25fps)

    // If we moved away from the last paused marker, clear guard
    if (lastPauseAtRef.current != null && Math.abs(now - lastPauseAtRef.current) > 0.25) {
      lastPauseAtRef.current = null;
    }

    // Find the first marker crossed between prev and now
    const crossed = markerTimes.find((mt) => mt > prev + EPS / 2 && mt <= now + EPS);
    if (crossed == null) return;

    // Avoid pausing multiple times at the same marker
    if (lastPauseAtRef.current != null && Math.abs(crossed - lastPauseAtRef.current) < EPS) return;

    try { el.currentTime = crossed; } catch { }
    try { el.pause(); } catch { }
    setIsPlaying(false);
    lastPauseAtRef.current = crossed;
  }, [current, markerTimes, isPlaying, ref]);

  if (!src) return null;

  // Router that distinguishes preview vs final payloads from AnnotationStage
  const handleSetNewAnnotation = (payload) => {
    // Accept new draws even if a pending box exists; latest draw will replace pending on mouse-up
    if (!payload) { setNewAnnotation(null); return; }

    // Final (normalized) box from AnnotationStage mouse-up
    if (payload.markerDetails && payload.markerDetails.coordinates) {
      const coords = payload.markerDetails.coordinates;
      // Notify parent; do NOT commit to Redux or preview here
      if (typeof onPendingAnnotationChange === 'function') {
        try { onPendingAnnotationChange(coords); } catch { }
      }
      // Exit drawing state
      setDrawing(false);
      return;
    }

    // Stage-coords preview update (during drag) — render preview rectangle
    setNewAnnotation(payload);
  };

  return (
    <div
      ref={fsContainerRef}
      style={{ position: 'relative', width: '100%', height: '100%' }}
      className={className}
    >
      <video
        ref={ref}
        poster={poster}
        controls={false}
        muted={muted}
        playsInline
        crossOrigin="anonymous"
        preload="auto"
        onContextMenu={(e) => { e.preventDefault(); }}
        onDragStart={(e) => { e.preventDefault(); }}
        disablePictureInPicture
        disableRemotePlayback
        controlsList="nodownload noplaybackrate noremoteplayback"
        onLoadedData={() => {
          // We can add specific logic here if needed
        }}
        style={{
          ...style,
          width: '100%',
          height: isPlaying ? '100%' : '90%',
          minHeight: isPlaying ? 180 : undefined,
          objectFit: 'contain',
          background: '#000',
          transition: 'height 200ms ease',
          userSelect: 'none',
          WebkitUserSelect: 'none',
          WebkitTouchCallout: 'none',
          MozUserSelect: 'none',
          msUserSelect: 'none'
        }}
      >
        {src && <source src={src} type="video/mp4" />}
      </video>

      {/* Control Bar - two rows, timeline on top to mirror VideojsPlayer */}
      {controls && (
        <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, background: 'linear-gradient(to top, rgba(0,0,0,0.65), rgba(0,0,0,0))', padding: '8px 10px 2px 10px', color: '#fff', display: 'flex', flexDirection: 'column', gap: 6 }}>
          {/* Row 1: progressControl with markers (thin bar + white knob) */}
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <div onClick={handleProgressClick} style={{ position: 'relative', height: 6, background: 'rgba(255,255,255,0.3)', borderRadius: 3, cursor: 'pointer', flex: 1 }}>
              <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: `${duration ? (current / duration) * 100 : 0}%`, background: '#ffffffc9', borderRadius: 3 }} />
              {/* knob */}
              <div style={{ position: 'absolute', top: '50%', left: `${duration ? (current / duration) * 100 : 0}%`, transform: 'translate(-50%, -50%)', width: 10, height: 10, background: '#fff', borderRadius: '50%' }} />
              {markerPercents.map((m, idx) => (
                <div
                  key={`m2-${idx}`}
                  title={`${m.label} @ ${m.pct.toFixed(1)}%`}
                  style={{ position: 'absolute', top: -8, height: 20, width: 5, background: '#dc3545', borderRadius: 2, left: `calc(${m.pct}% - 2.5px)` }}
                />
              ))}
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
            {/* SeekBackward */}
            <button type="button" onClick={() => seekBy(-5)} style={btnStyle} aria-label="Seek Backward 5s" {...hoverHandlers}>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="white" strokeWidth="2" xmlns="http://www.w3.org/2000/svg">
                <polygon points="17,4 9,10 17,16" fill="white" stroke="white" />
                <polygon points="11,4 3,10 11,16" fill="white" stroke="white" />
              </svg>
            </button>
            {/* playToggle */}
            <button type="button" onClick={togglePlay} style={btnStyle} aria-label="Play/Pause" {...hoverHandlers}>
              {isPlaying ? (
                <svg width="20" height="20" viewBox="0 0 20 20" fill="white" xmlns="http://www.w3.org/2000/svg">
                  <rect x="5" y="4" width="4" height="12" rx="1" />
                  <rect x="11" y="4" width="4" height="12" rx="1" />
                </svg>
              ) : (
                <svg width="20" height="20" viewBox="0 0 20 20" fill="white" xmlns="http://www.w3.org/2000/svg">
                  <polygon points="6,4 16,10 6,16" />
                </svg>
              )}
            </button>
            {/* SeekForward */}
            <button type="button" onClick={() => seekBy(5)} style={btnStyle} aria-label="Seek Forward 5s" {...hoverHandlers}>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="white" strokeWidth="2" xmlns="http://www.w3.org/2000/svg">
                <polygon points="3,4 11,10 3,16" fill="white" stroke="white" />
                <polygon points="9,4 17,10 9,16" fill="white" stroke="white" />
              </svg>
            </button>

            {/* volumePanel */}
            <button type="button" onClick={toggleMute} style={btnStyle} aria-label="Mute/Unmute" {...hoverHandlers}>
              {mutedState ? (
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="white" strokeWidth="2" xmlns="http://www.w3.org/2000/svg">
                  <path d="M3 8H6L10 5V15L6 12H3V8Z" fill="white" stroke="white" />
                  <line x1="14" y1="6" x2="18" y2="14" />
                </svg>
              ) : (
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="white" strokeWidth="2" xmlns="http://www.w3.org/2000/svg">
                  <path d="M3 8H6L10 5V15L6 12H3V8Z" fill="white" stroke="white" />
                  <path d="M13 8C14.5 9.5 14.5 10.5 13 12" />
                </svg>
              )}
            </button>
            {/* Hidden slider for volume parity */}
            <input type="range" min={0} max={1} step={0.05} value={mutedState ? 0 : volume} onChange={(e) => changeVolume(Number(e.target.value))} aria-label="Volume" style={{ display: 'none' }} />

            {/* playbackRateMenuButton */}
            <button type="button" onClick={() => {
              const rates = [0.1, 0.2, 0.5, 1, 2, 3, 5];
              const idx = rates.indexOf(rate);
              const next = rates[(idx + 1) % rates.length];
              changeRate(next);
            }} style={btnStyle} aria-label="Playback rate" {...hoverHandlers}>
              <span style={{ color: '#fff' }}>{`${rate}x`}</span>
            </button>

            {/* ModesButton (coach side only) */}
            {!isPlayerSide && (
              <button type="button" onClick={fireModesEvent} style={btnStyle} aria-label="Settings (Modes)" {...hoverHandlers}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33h.09A1.65 1.65 0 0 0 11 3.09V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82v.09a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  <circle cx="12" cy="12" r="3" fill="none" stroke="#B0B0B0" strokeWidth="1.5" />
                </svg>
              </button>
            )}

            {/* No explicit draw toggle; drawing is enabled by pausing the video (isPaused) */}

            {/* ScreenshotButton */}
            <button type="button" onClick={takeScreenshot} style={btnStyle} aria-label="Screenshot" {...hoverHandlers}>
              <svg width="20" height="20" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="4" y="16" width="56" height="40" rx="8" fill="white" stroke="black" strokeWidth="5" />
                <circle cx="32" cy="36" r="15" fill="black" stroke="black" strokeWidth="5" />
                <rect x="18" y="4" width="28" height="14" rx="4" fill="white" stroke="black" strokeWidth="5" />
              </svg>
            </button>

            {/* KeyboardHelpButton */}
            <button type="button" onClick={showKeyboardShortcuts} style={btnStyle} aria-label="Keyboard Help" {...hoverHandlers}>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="2" y="4" width="16" height="12" rx="2" stroke="white" strokeWidth="2" fill="none" />
                <rect x="4" y="6" width="2" height="2" fill="white" />
                <rect x="7" y="6" width="2" height="2" fill="white" />
                <rect x="10" y="6" width="2" height="2" fill="white" />
                <rect x="13" y="6" width="2" height="2" fill="white" />
                <rect x="4" y="9" width="2" height="2" fill="white" />
                <rect x="7" y="9" width="2" height="2" fill="white" />
                <rect x="10" y="9" width="2" height="2" fill="white" />
                <rect x="13" y="9" width="2" height="2" fill="white" />
                <rect x="4" y="12" width="2" height="2" fill="white" />
                <rect x="7" y="12" width="2" height="2" fill="white" />
                <rect x="10" y="12" width="2" height="2" fill="white" />
                <rect x="13" y="12" width="2" height="2" fill="white" />
              </svg>
            </button>

            {/* PictureInPictureToggle */}
            <button type="button" onClick={togglePiP} style={btnStyle} aria-label="Picture in Picture" {...hoverHandlers}>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="white" strokeWidth="2" xmlns="http://www.w3.org/2000/svg">
                <rect x="2" y="4" width="16" height="12" rx="2" stroke="white" />
                <rect x="10" y="9" width="6" height="4" fill="white" stroke="white" />
              </svg>
            </button>

            {/* fullscreenToggle */}
            <button type="button" onClick={toggleFullscreen} style={btnStyle} aria-label="Fullscreen" {...hoverHandlers}>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="white" strokeWidth="2" xmlns="http://www.w3.org/2000/svg">
                <polyline points="7,3 3,3 3,7" />
                <polyline points="13,3 17,3 17,7" />
                <polyline points="3,13 3,17 7,17" />
                <polyline points="17,13 17,17 13,17" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Annotation overlay */}
      {showAnnotations && (
        <AnnotationStage
          videoRef={ref}
          playerRef={playerRefShim}
          annotations={annotations}
          newAnnotation={newAnnotation}
          setNewAnnotation={(norm) => handleSetNewAnnotation(norm)}
          drawing={drawing}
          setDrawing={setDrawing}
          isPaused={!isPlaying}
          videoDimensions={null}
          onContentRectChange={() => { }}
          isPlayerSide={isPlayerSide}
          absoluteMode={true}
        />
      )}
    </div>
  );
}

// UI helpers
const hoverHandlers = {
  onMouseEnter: (e) => { try { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.1)'; } catch { } },
  onMouseLeave: (e) => { try { e.currentTarget.style.backgroundColor = 'transparent'; } catch { } },
  onFocus: (e) => { try { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.1)'; } catch { } },
  onBlur: (e) => { try { e.currentTarget.style.backgroundColor = 'transparent'; } catch { } },
};
const btnStyle = {
  background: 'transparent',
  color: '#fff',
  border: 'none',
  borderRadius: 4,
  padding: 0,
  width: 40,
  height: 40,
  minWidth: 40,
  minHeight: 40,
  margin: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
};
const selStyle = { display: 'none' };

// no-op
